/**
 * Plain-text receipt formatter for thermal printers (PT-210 / 80mm / 48 chars).
 *
 * Why plain text?
 *   HTML+CSS receipts go through the Android print pipeline (Chrome → PDF
 *   → RAWBT virtual printer). Chrome ignores `@page size: 80mm` on mobile
 *   and emits an A4 PDF, which RAWBT then shrinks to fit 80mm → ~38% scale
 *   → microscopic, faint output. Bypassing the print pipeline via the
 *   `rawbt:base64,<plaintext>` URI scheme delivers pixel-perfect output
 *   directly to the printer driver.
 *
 *   Plain text uses the printer's built-in font (Font A: 48 chars/line on
 *   80mm at 12×24 dot matrix), so there is no scaling, no rasterization,
 *   and no Android print-engine bugs to fight.
 *
 * RAWBT URI scheme:
 *   `rawbt:base64,<base64-of-utf8-bytes>` — universal across RAWBT versions.
 *   The helper `buildRawbtUri()` returns the full URI ready for an <a href>.
 */

const LINE_WIDTH = 48;
const DELIVERY_TYPE_LABELS = {
  takeaway: 'ELVITEL',
  delivery: 'HAZHOZSZALLITAS',
  dinein: 'HELYBEN FOGYASZTAS',
};
const PAYMENT_LABELS = {
  cash: 'Keszpenz atvetelkor',
  card: 'Bankkartya atvetelkor',
  szep_otp: 'OTP SZEP-kartya atvetelkor',
};

/**
 * ASCII transliteration of Hungarian accented characters.
 *
 * Why ASCII?
 *   The PT-210 / RAWBT pipeline renders our UTF-8 plaintext through the
 *   printer's active codepage (typically CP437 or CP852). Multi-byte
 *   UTF-8 sequences like `ő` (0xC5 0x91) get interpreted as two separate
 *   codepage glyphs -> mojibake on paper ("L|íngos", "tejf|Âl|Âs").
 *
 *   On top of that, JavaScript's String.length counts UTF-16 code units
 *   ('ő'.length === 1), while the printer steps one cell per byte. Any
 *   right-aligned price or two-column row computed with .length therefore
 *   ends up misaligned once accented chars appear -> mid-value wrap
 *   ("1890" + newline + "Ft").
 *
 *   ASCII transliteration solves both at once: byte count equals char
 *   count, no codepage surprises, no alignment drift. Restaurant context
 *   makes "tejfolos langos" perfectly readable.
 */
const TRANSLIT_MAP = {
  'á': 'a', 'é': 'e', 'í': 'i', 'ó': 'o', 'ö': 'o', 'ő': 'o',
  'ú': 'u', 'ü': 'u', 'ű': 'u',
  'Á': 'A', 'É': 'E', 'Í': 'I', 'Ó': 'O', 'Ö': 'O', 'Ő': 'O',
  'Ú': 'U', 'Ü': 'U', 'Ű': 'U',
};

const transliterateHungarian = (text) =>
  String(text ?? '').replace(/[áéíóöőúüűÁÉÍÓÖŐÚÜŰ]/g, (ch) => TRANSLIT_MAP[ch] || ch);

// Short alias used inside formatters — keeps the data-flow visually obvious:
// any user-supplied string passes through t() before it touches the buffer.
const t = transliterateHungarian;

/**
 * Byte length in UTF-8. After transliteration every char is 1 byte, but
 * we keep this defensive check in case a non-mapped multi-byte char ever
 * slips through (e.g. a future menu item with `ç`, `Ñ`, an emoji, etc.).
 * Without this, a stray 2-byte char would silently misalign the row.
 */
const byteLength = (s) => {
  if (typeof TextEncoder !== 'undefined') {
    return new TextEncoder().encode(String(s ?? '')).length;
  }
  // eslint-disable-next-line no-undef
  if (typeof Buffer !== 'undefined') {
    // eslint-disable-next-line no-undef
    return Buffer.byteLength(String(s ?? ''), 'utf8');
  }
  return String(s ?? '').length;
};

const repeat = (ch, n) => ch.repeat(Math.max(0, n));
const dividerThin = () => repeat('-', LINE_WIDTH);
const dividerThick = () => repeat('=', LINE_WIDTH);

const center = (text) => {
  const s = String(text ?? '');
  const len = byteLength(s);
  if (len >= LINE_WIDTH) return s.slice(0, LINE_WIDTH);
  const pad = Math.floor((LINE_WIDTH - len) / 2);
  return repeat(' ', pad) + s;
};

/**
 * Two-column row: label on the left, value right-aligned. If the combined
 * byte-length exceeds LINE_WIDTH, the value wraps to the next line, right-
 * aligned on its own line.
 */
const row = (left, right) => {
  const l = String(left ?? '');
  const r = String(right ?? '');
  const lLen = byteLength(l);
  const rLen = byteLength(r);
  const gap = LINE_WIDTH - lLen - rLen;
  if (gap >= 1) {
    return l + repeat(' ', gap) + r;
  }
  return l + '\n' + repeat(' ', Math.max(0, LINE_WIDTH - rLen)) + r;
};

/**
 * Word-wrap a string to LINE_WIDTH (or a given width), preserving word
 * boundaries. Long single words are hard-broken at the boundary. All width
 * checks use byte length so multi-byte chars never split mid-character.
 */
const wrap = (text, width = LINE_WIDTH, indent = '') => {
  const s = String(text ?? '').replace(/\s+/g, ' ').trim();
  if (!s) return '';
  const effectiveWidth = width - indent.length;
  const lines = [];
  let current = '';
  const words = s.split(' ');
  for (const word of words) {
    if (byteLength(word) > effectiveWidth) {
      if (current) {
        lines.push(indent + current);
        current = '';
      }
      // Hard-break at char boundary; after transliteration char === byte.
      let remaining = word;
      while (byteLength(remaining) > effectiveWidth) {
        lines.push(indent + remaining.slice(0, effectiveWidth));
        remaining = remaining.slice(effectiveWidth);
      }
      current = remaining;
      continue;
    }
    if (!current) {
      current = word;
    } else if (byteLength(current) + 1 + byteLength(word) <= effectiveWidth) {
      current += ' ' + word;
    } else {
      lines.push(indent + current);
      current = word;
    }
  }
  if (current) lines.push(indent + current);
  return lines.join('\n');
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  try {
    return new Date(dateString).toLocaleString('hu-HU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Budapest',
    });
  } catch {
    return '';
  }
};

const formatMoney = (n) => `${Number(n ?? 0).toLocaleString('hu-HU')} Ft`;

const formatItem = (item) => {
  const qty = item.quantity ?? 1;
  const price = item.price ?? 0;
  const lineTotal = qty * price;
  // Every user-supplied string flows through t() so byte-length === char-length
  // by the time it reaches row()/wrap(). formatMoney() output is already ASCII.
  const header = row(t(`${qty}x ${item.name || ''}`), formatMoney(lineTotal));

  const extras = [];
  if (Array.isArray(item.selectedOptions) && item.selectedOptions.length > 0) {
    extras.push(wrap(t(`+ ${item.selectedOptions.join(', ')}`), LINE_WIDTH, '   '));
  }
  if (Array.isArray(item.selectedExtras) && item.selectedExtras.length > 0) {
    extras.push(wrap(t(`Extrak: ${item.selectedExtras.join(', ')}`), LINE_WIDTH, '   '));
  }
  if (item.selectedSpice && item.selectedSpice !== 'Normál' && item.selectedSpice !== 'Normal') {
    extras.push(wrap(t(`[CSIPOS] ${item.selectedSpice}`), LINE_WIDTH, '   '));
  }
  if (item.customerNote) {
    extras.push(wrap(t(`! MEGJEGYZES: ${String(item.customerNote).toUpperCase()}`), LINE_WIDTH, '   '));
  }
  return [header, ...extras].join('\n');
};

/**
 * Build the full plain-text receipt for a given order.
 * Returns a single string ready to be base64-encoded for `rawbt:` URI.
 */
export const formatReceiptPlainText = (order) => {
  if (!order) return '';

  const deliveryType = DELIVERY_TYPE_LABELS[order.deliveryType]
    || String(order.deliveryType || '').toUpperCase();
  const paymentLabel = PAYMENT_LABELS[order.paymentMethod] || order.paymentMethod || '';

  const lines = [];

  // === HEADER ===
  lines.push(dividerThick());
  lines.push(center('BOLDOG BUFE'));
  lines.push(center('Ha itt eszel, boldog leszel'));
  lines.push(dividerThin());

  // === ORDER META ===
  lines.push(row('Rendelesszam:', t(order.orderNumber || '')));
  lines.push(row('Datum:', t(formatDate(order.createdAt))));
  lines.push('');

  // === CUSTOMER ===
  lines.push(row('Nev:', t(order.customerName || '')));
  lines.push(row('Telefon:', t(order.customerPhone || '')));
  lines.push(row('Tipus:', deliveryType));
  if (order.deliveryType === 'delivery') {
    if (order.deliveryZone) {
      lines.push(row('Zona:', t(order.deliveryZone)));
    }
    if (order.deliveryAddress) {
      lines.push('Cim:');
      lines.push(wrap(t(order.deliveryAddress), LINE_WIDTH, '   '));
    }
  }
  lines.push(dividerThin());

  // === ITEMS ===
  lines.push(center('RENDELT TETELEK'));
  lines.push(dividerThin());
  if (Array.isArray(order.items)) {
    order.items.forEach((item, idx) => {
      lines.push(formatItem(item));
      if (idx < order.items.length - 1) lines.push('');
    });
  }
  lines.push(dividerThin());

  // === ORDER NOTE ===
  if (order.notes) {
    lines.push('!!! RENDELES MEGJEGYZES:');
    lines.push(wrap(t(String(order.notes).toUpperCase()), LINE_WIDTH));
    lines.push(dividerThin());
  }

  // === TOTALS ===
  if (order.cartSubtotal) {
    lines.push(row('Etelek:', formatMoney(order.cartSubtotal)));
  }
  if (order.packagingTotal > 0) {
    lines.push(row('Csomagolas:', formatMoney(order.packagingTotal)));
  }
  if (order.deliveryFee > 0) {
    lines.push(row('Szallitas:', formatMoney(order.deliveryFee)));
  }
  lines.push(dividerThick());
  lines.push(row('VEGOSSZEG:', formatMoney(order.total)));
  lines.push(dividerThick());

  // === PAYMENT ===
  lines.push('');
  lines.push(center(`Fizetes: ${paymentLabel}`));
  lines.push('');

  // === FOOTER ===
  lines.push(center('Koszonjuk a rendeleset!'));
  lines.push(center('Jo etvagyat kivanunk!'));
  lines.push(dividerThin());
  lines.push(center('Boldog Bufe'));
  lines.push(center('Szechenyi u. 44, Szigethalom'));
  lines.push(center('+36 30 951 7159'));
  lines.push(dividerThin());
  lines.push(center('Ha itt eszel, boldog leszel'));
  lines.push(dividerThick());

  // Feed a few extra lines so the printer's tear-off bar reaches past the
  // last text line. Without these, the user has to manually advance the paper.
  lines.push('');
  lines.push('');
  lines.push('');

  return lines.join('\n');
};

/**
 * UTF-8-safe base64 encoder. `btoa` only handles Latin-1; this two-step
 * trick (encodeURIComponent → unescape → btoa) survives Hungarian chars.
 */
const utf8Base64 = (str) => {
  // eslint-disable-next-line no-undef
  return btoa(unescape(encodeURIComponent(str)));
};

/**
 * Build the full `rawbt:base64,<...>` URI for an order.
 * Use as: <a href={buildRawbtUri(order)}>Print</a>
 */
export const buildRawbtUri = (order) => {
  const text = formatReceiptPlainText(order);
  return `rawbt:base64,${utf8Base64(text)}`;
};

export default formatReceiptPlainText;
